import { ReebeloConditions } from '@lambda/reebelo';
import { AvailableVariantT } from '@lambda/apis/src/catalog-v2/types';
import { stripDuplicateTitleHeading } from './sections/secondary/helpers';

/**
 * Google asks for a price-freshness signal on refurbished goods. A rolling
 * month keeps the schema honest without promising a price we may revise well
 * before then.
 */
/**
 * RT-17610: the PDP's absolute URL. Some catalogue handles contain "/"
 * (e.g. `…-for-iphone-6/7/8/se`), which only resolves once encoded as %2F;
 * written raw it 404s, so every URL we publish for a product goes through here.
 */
export const buildProductUrl = (domain: string, productId: string): string =>
  `https://${domain}/collections/${encodeURIComponent(productId)}`;

export const PRICE_VALID_DAYS = 30;

export const NEW_CONDITION = 'https://schema.org/NewCondition';
export const REFURBISHED_CONDITION = 'https://schema.org/RefurbishedCondition';
export const IN_STOCK = 'https://schema.org/InStock';
export const OUT_OF_STOCK = 'https://schema.org/OutOfStock';

/**
 * `priceValidUntil` as a plain date, always in UTC so a server render and the
 * hydrating client agree on it.
 */
export const getPriceValidUntil = (from: Date = new Date()): string => {
  const until = new Date(from.getTime());

  until.setUTCDate(until.getUTCDate() + PRICE_VALID_DAYS);

  return until.toISOString().slice(0, 10);
};

export const getPriceValidFrom = (from: Date = new Date()): string =>
  from.toISOString().slice(0, 10);

/** Google caps a merchant listing description at 5,000 characters. */
export const DESCRIPTION_MAX_LENGTH = 5000;

export const toPlainDescription = (markdown: string): string =>
  markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s*/gm, '')
    .replace(/^\s{0,3}[-*+]\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const buildSchemaDescription = ({
  description,
  fallback,
  productName,
  maxLength = DESCRIPTION_MAX_LENGTH,
}: {
  description?: string;
  fallback?: string;
  productName?: string;
  maxLength?: number;
}): string | undefined => {
  const authored = toPlainDescription(
    stripDuplicateTitleHeading(description ?? '', productName),
  );
  const text = authored || (fallback ?? '').trim();

  if (!text) {
    return undefined;
  }

  if (text.length <= maxLength) {
    return text;
  }

  // Cut on a word boundary and keep the ellipsis inside the budget.
  return `${text.slice(0, maxLength - 1).replace(/\s+\S*$/, '')}…`;
};

const formatPrice = (cents: number): string => (cents / 100).toFixed(2);

type QuantitativeValueT = {
  '@type': 'QuantitativeValue';
  minValue: number;
  maxValue: number;
  unitCode: 'DAY';
};

export type ShippingDetailsT = {
  '@type': 'OfferShippingDetails';
  shippingRate: {
    '@type': 'MonetaryAmount';
    value: number;
    currency: string;
  };
  shippingDestination: {
    '@type': 'DefinedRegion';
    addressCountry: string;
  };
  deliveryTime?: {
    '@type': 'ShippingDeliveryTime';
    handlingTime: QuantitativeValueT;
    transitTime: QuantitativeValueT;
  };
};

export type ReturnPolicyT = {
  '@type': 'MerchantReturnPolicy';
  applicableCountry: string;
  returnPolicyCategory: string;
  merchantReturnDays: number;
  returnMethod: string;
  returnFees: string;
};

export type ProductOfferT = {
  '@type': 'Offer';
  price: string;
  priceCurrency: string;
  itemCondition: string;
  availability: string;
  validFrom: string;
  priceValidUntil: string;
  url: string;
  seller: { '@type': 'Organization'; name: string };
  shippingDetails?: ShippingDetailsT;
  hasMerchantReturnPolicy?: ReturnPolicyT;
};

const days = (minValue: number, maxValue: number): QuantitativeValueT => ({
  '@type': 'QuantitativeValue',
  minValue,
  maxValue,
  unitCode: 'DAY',
});

/**
 * Shipping as Merchant Center wants to read it: what it costs, where it goes,
 * and how long it takes. Delivery is free on every Reebelo store, and the
 * handling/transit split comes from the offer's own estimate — omitted when
 * the catalogue has none, since a guessed delivery time is worse than none.
 */
export const buildShippingDetails = ({
  priceCurrency,
  addressCountry,
  handlingTime,
  transitTime,
}: {
  priceCurrency: string;
  addressCountry: string;
  handlingTime?: number;
  transitTime?: { min: number; max: number };
}): ShippingDetailsT => ({
  '@type': 'OfferShippingDetails',
  shippingRate: {
    '@type': 'MonetaryAmount',
    value: 0,
    currency: priceCurrency,
  },
  shippingDestination: {
    '@type': 'DefinedRegion',
    addressCountry,
  },
  ...(transitTime
    ? {
        deliveryTime: {
          '@type': 'ShippingDeliveryTime' as const,
          handlingTime: days(handlingTime ?? 0, handlingTime ?? 0),
          transitTime: days(transitTime.min, transitTime.max),
        },
      }
    : {}),
});

/**
 * The returns window as a MerchantReturnPolicy. `returnDays` is the store's own
 * figure, so a market with a shorter window states the shorter window.
 */
export const buildReturnPolicy = ({
  addressCountry,
  returnDays,
}: {
  addressCountry: string;
  returnDays: number;
}): ReturnPolicyT => ({
  '@type': 'MerchantReturnPolicy',
  applicableCountry: addressCountry,
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: returnDays,
  returnMethod: 'https://schema.org/ReturnByMail',
  returnFees: 'https://schema.org/FreeReturn',
});

const SELLER = { '@type': 'Organization' as const, name: 'Reebelo' };

const conditionOf = (variant: AvailableVariantT): string | undefined =>
  variant.variants.find((v) => v.variant.slug === 'condition')?.value?.value;

export const itemConditionFor = (
  condition: string | undefined,
  forceNewCondition = false,
): string =>
  forceNewCondition || condition === ReebeloConditions.BrandNew
    ? NEW_CONDITION
    : REFURBISHED_CONDITION;

/** The schema.org axes Google accepts in `variesBy`. */
export const VARIES_BY_COLOR = 'https://schema.org/color';
export const VARIES_BY_SIZE = 'https://schema.org/size';

const valueOf = (
  variant: AvailableVariantT,
  slug: string,
): string | undefined => {
  const value = variant.variants.find((v) => v.variant.slug === slug)?.value;

  return (value?.displayValue || value?.value)?.trim() || undefined;
};

export type ProductVariantNodeT = {
  '@type': 'Product';
  sku: string;
  gtin?: string;
  mpn?: string;
  name: string;
  description?: string;
  brand?: { '@type': 'Brand'; name: string };
  image?: string[];
  color?: string;
  size?: string;
  offers: ProductOfferT;
};

/**
 * The SKU's own GTIN, or its MPN where it has no GTIN — the identifier Google
 * matches the offer to its catalogue by. Nothing at all when it has neither.
 */
const identifierFor = ({
  identifiers,
}: AvailableVariantT):
  | { gtin: string }
  | { mpn: string }
  | Record<string, never> => {
  if (identifiers?.gtin) {
    return { gtin: identifiers.gtin };
  }

  if (identifiers?.mpn) {
    return { mpn: identifiers.mpn };
  }

  return {};
};

/** The display value of every axis other than colour and storage. */
const otherAxesOf = (variant: AvailableVariantT): string[] =>
  variant.variants
    .filter((v) => v.variant.slug !== 'color' && v.variant.slug !== 'storage')
    .map((v) => (v.value?.displayValue || v.value?.value)?.trim())
    .filter((value): value is string => !!value);

export const buildProductVariants = ({
  variants,
  productName,
  description,
  brand,
  imagesForSku,
  priceCurrency,
  productUrl,
  forceNewCondition,
  isOutOfStock,
  shippingDetails,
  hasMerchantReturnPolicy,
  validFrom = getPriceValidFrom(),
  priceValidUntil = getPriceValidUntil(),
}: {
  variants: AvailableVariantT[];
  productName: string;
  description?: string;
  brand?: string;
  /** That SKU's own images, falling back to the product's where it has none. */
  imagesForSku: (skuId: string) => string[];
  priceCurrency: string;
  productUrl: string;
  forceNewCondition: boolean;
  isOutOfStock: boolean;
  shippingDetails?: ShippingDetailsT;
  hasMerchantReturnPolicy?: ReturnPolicyT;
  validFrom?: string;
  priceValidUntil?: string;
}): ProductVariantNodeT[] | undefined => {
  // One variant per SKU, so each matches its own GMC feed item (sku, price,
  // item_group_id) — eSIM and physical SIM included. GTIN/MPN come from
  // available-skus itself, so this costs no extra request.
  const nodes = variants
    // A merchant listing needs a price above zero; a priceless SKU would
    // invalidate its variant rather than add one.
    .filter((variant) => variant.price > 0)
    .map((variant) => {
      const color = valueOf(variant, 'color');
      const size = valueOf(variant, 'storage');
      const images = imagesForSku(variant.skuId);

      return {
        '@type': 'Product' as const,
        sku: variant.skuId,
        ...identifierFor(variant),
        // The group's name plus the axes that tell this SKU from its siblings.
        name: [productName, color, size, ...otherAxesOf(variant)]
          .filter(Boolean)
          .join(' - '),
        description,
        brand: brand ? { '@type': 'Brand' as const, name: brand } : undefined,
        image: images.length > 0 ? images : undefined,
        color,
        size,
        offers: {
          '@type': 'Offer' as const,
          price: formatPrice(variant.price),
          priceCurrency,
          itemCondition: itemConditionFor(
            conditionOf(variant),
            forceNewCondition,
          ),
          availability: isOutOfStock ? OUT_OF_STOCK : IN_STOCK,
          validFrom,
          priceValidUntil,
          url: `${productUrl}?skuId=${encodeURIComponent(variant.skuId)}`,
          seller: SELLER,
          shippingDetails,
          hasMerchantReturnPolicy,
        },
      };
    });

  return nodes.length > 0 ? nodes : undefined;
};

export const buildVariesBy = (
  variants: ProductVariantNodeT[] | undefined,
): string[] | undefined => {
  const axes = [
    {
      value: VARIES_BY_COLOR,
      has: (v: ProductVariantNodeT) => v.color != null,
    },
    { value: VARIES_BY_SIZE, has: (v: ProductVariantNodeT) => v.size != null },
  ]
    .filter(({ has }) => (variants ?? []).some(has))
    .map(({ value }) => value);

  return axes.length > 0 ? axes : undefined;
};

export type ProductSchemaReviewT = {
  author: string;
  datePublished: string;
  reviewBody: string;
  name: string;
  reviewRating: {
    bestRating: number;
    ratingValue: number;
    worstRating: number;
  };
};

/**
 * Reviews are collected and shown through Trustpilot (the PDP carries its
 * badge), so Trustpilot is the organization that published them.
 */
const REVIEW_PUBLISHER = {
  '@type': 'Organization',
  name: 'Trustpilot',
  url: 'https://www.trustpilot.com',
} as const;

export const serialiseJsonLd = (value: unknown): string =>
  JSON.stringify(value).replace(/</g, '\\u003c');

// The FAQPage node for a product, or null when it has no product-specific
// questions worth publishing.
export const buildFaqPageJsonLd = (
  entities: { questionName: string; acceptedAnswerText: string }[],
) =>
  entities.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: entities.map(({ questionName, acceptedAnswerText }) => ({
          '@type': 'Question',
          name: questionName,
          acceptedAnswer: {
            '@type': 'Answer',
            text: acceptedAnswerText,
          },
        })),
      }
    : null;

export const buildProductFaqEntities = (
  faq: { question: string; answer: string }[] | undefined,
  productName: string,
): { questionName: string; acceptedAnswerText: string }[] => {
  const needle = productName.trim().toLowerCase();

  if (!needle) {
    return [];
  }

  const seen = new Set<string>();

  return (faq ?? []).flatMap(({ question, answer }) => {
    const questionName = question?.trim();
    const acceptedAnswerText = answer?.trim();

    if (!questionName || !acceptedAnswerText) {
      return [];
    }

    if (!questionName.toLowerCase().includes(needle)) {
      return [];
    }

    const key = questionName.toLowerCase();

    if (seen.has(key)) {
      return [];
    }

    seen.add(key);

    return [{ questionName, acceptedAnswerText }];
  });
};

export const buildProductJsonLd = ({
  productName,
  description,
  images,
  brand,
  manufacturerName,
  reviews,
  aggregateRating,
  productGroupID,
  url,
  hasVariant,
}: {
  productName: string;
  description?: string;
  images: string[];
  brand?: string;
  manufacturerName?: string;
  reviews?: ProductSchemaReviewT[];
  aggregateRating: { ratingValue: number; reviewCount: number };
  productGroupID?: string;
  url: string;
  hasVariant?: ProductVariantNodeT[];
}) => ({
  '@context': 'https://schema.org',
  '@type': 'ProductGroup',
  name: productName,
  description,
  productGroupID,
  url,
  variesBy: buildVariesBy(hasVariant),
  // RT-17607: an empty list is invalid markup, so a product with no photos
  // leaves the field out, as its variants already do.
  image: images.length > 0 ? images : undefined,
  brand: brand ? { '@type': 'Brand', name: brand } : undefined,
  manufacturer: manufacturerName
    ? { '@type': 'Organization', name: manufacturerName }
    : undefined,
  review: reviews?.map(({ author, reviewRating, ...rest }) => ({
    ...rest,
    '@type': 'Review',
    author: { '@type': 'Person', name: author },
    reviewRating: { ...reviewRating, '@type': 'Rating' },
    publisher: REVIEW_PUBLISHER,
  })),
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: aggregateRating.ratingValue,
    reviewCount: aggregateRating.reviewCount,
  },
  hasVariant,
});
